import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Slide, Fade } from "@mui/material";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { Col, Container, Form, Row, Stack, InputGroup } from "react-bootstrap";
import ListHeading from "../../../common/RDTListHeading/ListHeading";
import BreadcrumbHeading from "../../../common/RDTListHeading/BreadcrumbHeading";
import Images from "../../../../utils/images";
import GeneralButton from "../../../common/SaveButton/GeneralButton";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { showError, showSuccess } from "../../../../utils/showMessage";
import useWindowDimensions from "../../../../utils/getHeightWidth";
import { addCloudConfiguration, getCloudConfigurationById, updateCloudConfiguration, pollCloudConfigurationStatus } from "../../../../api/cloudDiscovery";
import Select from "react-select";
import Loader from "../../../common/Loader/Loader";
import GetCapabilities from "../../../../utils/getCapabilities.js";
import { useReactSelectTheme } from "../../../../utils/useReactSelectTheme";
import { useTranslation } from "react-i18next";

export default function CloudDiscoveryOperations() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const providerFromQuery = searchParams.get("provider");
  const operationType = id ? "edit" : "add";
  const [slideChecked, setSlideChecked] = useState(false);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [configurationName, setConfigurationName] = useState("");
  const [autoOnboard, setAutoOnboard] = useState(false);
  const [whatToScan, setWhatToScan] = useState([]);
  const [configId, setConfigId] = useState(null);
  const { height } = useWindowDimensions();
  const datatable_height = height - 120 + "px";
  const [awsAccessKeyId, setAwsAccessKeyId] = useState("");
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState("");
  const [awsRegion, setAwsRegion] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [provider, setProvider] = useState(providerFromQuery || "aws");
  const my_capabilities = GetCapabilities();
  const isCapable = my_capabilities[0];
  const { styles: selectStyles, theme: selectTheme } = useReactSelectTheme();
  const scanOptions = [
    { label: t("cloudDiscovery.scanOptions.ec2Instance"), value: "EC2", isDisabled: false },
    { label: t("cloudDiscovery.scanOptions.rdsInstance"), value: "RDS", isDisabled: true },
    { label: t("cloudDiscovery.scanOptions.vpc"), value: "VPC", isDisabled: true },
    { label: t("cloudDiscovery.scanOptions.s3Bucket"), value: "S3", isDisabled: true },
    { label: t("cloudDiscovery.scanOptions.lambda"), value: "LAMBDA", isDisabled: true },
    { label: t("cloudDiscovery.scanOptions.eksCluster"), value: "EKS", isDisabled: true },
    { label: t("cloudDiscovery.scanOptions.ecsCluster"), value: "ECS", isDisabled: true },
    { label: t("cloudDiscovery.scanOptions.dynamoDbTable"), value: "DYNAMODB", isDisabled: true },
    { label: t("cloudDiscovery.scanOptions.cloudWatchLogGroup"), value: "CLOUDWATCH", isDisabled: true },
  ];
  const handleSelectChange = (selectedOptions) => {
    const enabledOptions = (selectedOptions || []).filter(
      (option) => !option.isDisabled && (option.value === "EC2" || option.value === "RDS")
    );
    setWhatToScan(enabledOptions); 
  };

  const awsRegionNames = useMemo(() => ({
    "us-east-1": t("cloudDiscovery.regions.us-east-1"),
    "us-east-2": t("cloudDiscovery.regions.us-east-2"),
    "us-west-1": t("cloudDiscovery.regions.us-west-1"),
    "us-west-2": t("cloudDiscovery.regions.us-west-2"),
    "ap-south-1": t("cloudDiscovery.regions.ap-south-1"),
    "ap-southeast-1": t("cloudDiscovery.regions.ap-southeast-1"),
    "ap-southeast-2": t("cloudDiscovery.regions.ap-southeast-2"),
    "ap-northeast-1": t("cloudDiscovery.regions.ap-northeast-1"),
    "eu-central-1": t("cloudDiscovery.regions.eu-central-1"),
    "eu-west-1": t("cloudDiscovery.regions.eu-west-1"),
    "eu-west-2": t("cloudDiscovery.regions.eu-west-2"),
  }), [t]);

  const awsRegions = [
    "us-east-1",
    "us-east-2",
    "us-west-1",
    "us-west-2",
    "ap-south-1",
    "ap-southeast-1",
    "ap-southeast-2",
    "ap-northeast-1",
    "eu-central-1",
    "eu-west-1",
    "eu-west-2",
  ];

  const validateProviderFields = () => {
    if (!configurationName) {
      showError(t("cloudDiscovery.form.validation.enterConfigurationName"));
      return false;
    }
    if (!awsAccessKeyId) {
      showError(t("cloudDiscovery.form.validation.enterAwsAccessKeyId"));
      return false;
    }
    if (!awsSecretAccessKey) {
      showError(t("cloudDiscovery.form.validation.enterAwsSecretAccessKey"));
      return false;
    }
    if (!awsRegion) {
      showError(t("cloudDiscovery.form.validation.selectAwsRegion"));
      return false;
    }
    if (whatToScan.length === 0) {
      showError(t("cloudDiscovery.form.validation.selectAtLeastOneTarget"));
      return false;
    }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    if (!provider) {
      showError(t("cloudDiscovery.form.validation.selectCloudProvider"));
      return;
    }

    if (!validateProviderFields()) {
      setValidated(true);
      return;
    }

    if (operationType === "edit" && !configId) {
      showError(t("cloudDiscovery.form.validation.missingConfigId"));
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        configuration_name: configurationName,
        platform: provider,
        what_to_scan: whatToScan.map((item) => item.value),
        auto_onboard: autoOnboard,
        aws_access_key_id: awsAccessKeyId,
        aws_secret_access_key: awsSecretAccessKey,
        aws_region: awsRegion?.value || null,
      };

      let response;
      let savedConfigId = null;
      
      if (operationType === "edit" && configId) {
        response = await updateCloudConfiguration({ config_id: configId, ...payload });
        savedConfigId = response.data?.config?.id || configId;
      } else {
        response = await addCloudConfiguration(payload);
        if (response.data?.config?.id) {
          savedConfigId = response.data.config.id;
        }
      }
      const { data, error } = response;

      if (error) {
        showError(error?.errors?.message?.[0] || (typeof error === 'string' ? error : null) || t("cloudDiscovery.messages.saveFailed"));
      } else {
        showSuccess(
          data?.message ||
          (operationType === "edit"
            ? t("cloudDiscovery.messages.updateSuccess")
            : t("cloudDiscovery.messages.addSuccess"))
        );
        
        const configProvider = data?.config?.provider_type || data?.config?.platform || data?.provider_type || provider;
        const providerType = typeof configProvider === 'string' ? configProvider.toLowerCase() : 'aws';
        
        navigate("/dashboard/discovery/cloudconfiguration", { 
          state: { shouldRefresh: true, configId: savedConfigId } 
        });
        
        if (savedConfigId && providerType === 'aws') {
          setTimeout(async () => {
            try {
              await pollCloudConfigurationStatus({ config_id: savedConfigId });
              window.dispatchEvent(new CustomEvent('cloudConfigUpdated', { 
                detail: { configId: savedConfigId } 
              }));
            } catch (pollError) {
              console.error("Failed to poll status after save/update:", pollError);
            }
          }, 1000);
        }
      }
    } catch (err) {
      showError(t("cloudDiscovery.messages.saveFailed"));
    } finally {
      setLoading(false);
    }
  };

  const cancelAction = () => {
    navigate("/dashboard/discovery/cloudconfiguration");
  };

  useEffect(() => {
    document.title = operationType === "edit" ? t("cloudDiscovery.editTitle") : t("cloudDiscovery.addTitle");
    setSlideChecked(true);
    
    if (operationType === "edit" && !isCapable?.cloud_discovery?.edit) {
      navigate("/dashboard/discovery/cloudconfiguration");
      return;
    }
    if (operationType === "add" && !isCapable?.cloud_discovery?.add) {
      navigate("/dashboard/discovery/cloudconfiguration");
      return;
    }
    
    const init = async () => {
      if (operationType === "edit" && id) {
        try {
          const { data, error } = await getCloudConfigurationById({ config_id: id });
          if (error) {
            showError(error?.message?.[0] || t("cloudDiscovery.messages.loadFailed"));
            return;
          }
          if (data) {
            const cfg = data;
            setConfigId(cfg.id || "");
            setConfigurationName(cfg.configuration_name || cfg.name || "");
            setProvider(providerFromQuery || cfg.platform || "aws");
            setAutoOnboard(false);
            setWhatToScan(
              (cfg.what_to_scan || cfg.service || cfg.services || [])
                .map((val) =>
                  scanOptions.find((opt) => opt.value === (typeof val === "string" ? val : val.value))
                )
                .filter(Boolean)
                .filter((opt) => !opt.isDisabled && (opt.value === "EC2" || opt.value === "RDS"))
            );
            setAwsAccessKeyId(cfg.aws_access_key_id || "");
            setAwsSecretAccessKey(cfg.aws_secret_access_key || "");
            const regions = cfg.aws_region || cfg.region || cfg.regions || [];
            const regionValue = Array.isArray(regions) ? regions[0] : regions;
            if (regionValue) {
              const regionCode = typeof regionValue === "string" ? regionValue : (regionValue.value || regionValue);
              const regionName = awsRegionNames[regionCode] || regionCode;
              setAwsRegion({
                label: `${regionCode} - ${regionName}`,
                value: regionCode
              });
            } else {
              setAwsRegion(null);
            }
          }
        } catch (e) {
          showError(t("cloudDiscovery.messages.loadFailed") + ": " + (e.message || String(e)));
        }
      } else {
        setProvider(providerFromQuery || "aws");
      }
    };

    init();
  }, [operationType, id, providerFromQuery, isCapable, navigate, t]);

  return (
    <>
      <Loader loading={loading} bg_papper={false} />
      <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
        <div>
          <Fade timeout={fadedelayTime} in={slideChecked}>
          <Container fluid
            className="main_content_container mx-auto p-3">
            <div
              className="position-relative bg-white"
              style={{
                height: datatable_height,
                overflowY: "auto", overflowX: "hidden"
              }}
            >
              <div className="configure_mfa_heading">
                <div className="ms-4 ps-1 py-2 pt-1">
                  <BreadcrumbHeading
                    dataTableHeadingText={
                      operationType === "edit"
                        ? [
                            t("cloudDiscovery.breadcrumb.cloudDiscovery"),
                            t("cloudDiscovery.editTitle")
                          ]
                        : [
                            t("cloudDiscovery.breadcrumb.cloudDiscovery"),
                            t("cloudDiscovery.breadcrumb.selectPlatform"),
                            t("cloudDiscovery.addTitle")
                          ]
                    }
                    dataTablePageLink={
                      operationType === "edit"
                        ? [
                            "/dashboard/discovery/cloudconfiguration",
                            `/dashboard/discovery/cloudconfiguration/edit-cloudconfiguration/${id}`,
                          ]
                        : [
                            "/dashboard/discovery/cloudconfiguration",
                            "/dashboard/discovery/cloudconfiguration/select-plateform/add-configuration",
                            "/dashboard/discovery/cloudconfiguration/add-cloudconfiguration?provider=aws",
                          ]
                    }
                    pageNumber={operationType === "edit" ? 2 : 3}
                  />
                </div>
                <div className="p-2 d-flex flex-row justify-content-between w-100 ms-2">
                  <div className="d-flex align-items-center fw-300 ff-poppins text-wrap ms-3 mb-2">
                    <ListHeading
                      dataTableHeadingText={[
                        operationType === "edit"
                          ? t("cloudDiscovery.editTitle")
                          : t("cloudDiscovery.addTitle"),
                      ]}
                      dataTableHeadingIcon={Images.NetworkAnalyzer}
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 ms-2 mt-4 overflow-auto"
               style={{ height: height - 350 }}
              >
                <Form
                  noValidate
                  validated={validated}
                  onSubmit={handleSave}
                  className="add_cloud_configuration ms-1 "
                  autoComplete="off"
                  id="cloud_discovery_operations"
                >
                  <Stack gap={1}>
                    <Row className="pb-3">
                      <Col md={5}>
                        <Form.Group controlId="configurationName" >
                          <Form.Label className="ff-pam fs-14px">
                            {t("cloudDiscovery.form.configurationName")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            required
                            className="user_input_field"
                            type="text"
                            value={configurationName}
                            onChange={(e) => setConfigurationName(e.target.value)}
                            placeholder={t("cloudDiscovery.form.placeholder.enterConfigurationName")}
                            autoComplete="new-configuration name"
                          />
                          <Form.Control.Feedback type="invalid">
                            {t("cloudDiscovery.form.validation.enterConfigurationName")}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={5} className="ps-5">
                        <Form.Group controlId="awsAccessKeyId">
                          <Form.Label className="ff-pam fs-14px">
                            {t("cloudDiscovery.form.awsAccessId")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            required
                            className="user_input_field"
                            type="text"
                            value={awsAccessKeyId}
                            onChange={(e) => setAwsAccessKeyId(e.target.value.toUpperCase())}
                            placeholder={t("cloudDiscovery.form.placeholder.enterAccessKeyId")}
                            pattern="^(AKIA|ASIA|AGPA|AIDA)[A-Z0-9]{16}$"
                            maxLength={20}
                            minLength={20}
                            autoComplete="new-configuration name"
                          />
                          <Form.Control.Feedback type="invalid">
                            {t("cloudDiscovery.form.validation.awsAccessIdInvalid")}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="pb-3">
                      <Col md={5} >
                        <Form.Group >
                          <Form.Label
                            className="ff-pam fs-14px"
                          >
                            {t("cloudDiscovery.form.awsSecretAccessKey")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup>
                            <Form.Control
                              required
                              className="user_input_field"
                              type={showSecretKey ? "text" : "password"}
                              value={awsSecretAccessKey}
                              onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                              placeholder={t("cloudDiscovery.form.placeholder.enterSecretAccessKey")}
                              pattern="^[A-Za-z0-9\/+=]{40}$"
                              maxLength={40}
                              minLength={40}
                              autoComplete="aws password"
                            />
                            <InputGroup.Text
                              className="bg-white"
                              onClick={() => setShowSecretKey(!showSecretKey)}
                              style={{ cursor: "pointer" }}
                            >
                              {showSecretKey ? (
                                <LazyLoadImage
                                  src={Images.Hide_Text_Eye_Icon}
                                  height="20px"
                                  className="px-2 eye-icon"
                                  alt={t("common.hidePassword") || "Hide password"}
                                />
                              ) : (
                                <LazyLoadImage
                                  src={Images.Show_Text_Eye_Icon}
                                  height="20px"
                                  className="px-2 eye-icon"
                                  alt={t("common.showPassword") || "Show password"}
                                />
                              )}
                            </InputGroup.Text>
                          </InputGroup>
                          <Form.Control.Feedback type="invalid">
                            {t("cloudDiscovery.form.validation.awsSecretKeyInvalid")}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={5} className="ps-5">
                        <Form.Group >
                          <Form.Label
                            className="ff-pam fs-14px">
                            {t("cloudDiscovery.form.regionName")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Select
                            name="awsRegion"
                            options={awsRegions.map((r) => ({ 
                              label: `${r} - ${awsRegionNames[r] || r}`, 
                              value: r 
                            }))}
                            value={awsRegion}
                            onChange={(selectedOption) => setAwsRegion(selectedOption || null)}
                            placeholder={t("cloudDiscovery.form.placeholder.selectRegion")}
                            menuPlacement="bottom"
                            isClearable
                            styles={{
                              ...selectStyles,
                              control: (provided, state) => ({
                                ...selectStyles.control(provided, state),
                                minHeight: "38px",
                                fontSize: "14px",
                                paddingTop: "4px",
                                paddingBottom: "4px",
                              }),
                              menuList: (provided) => ({
                                ...selectStyles.menuList(provided),
                                maxHeight: 270,
                              }),
                            }}
                            theme={selectTheme}
                          />

                          <Form.Control.Feedback type="invalid">
                            {t("cloudDiscovery.form.validation.selectRegionName")}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="pb-3">
                      <Col md={5} >
                        <Form.Group >
                          <Form.Label className="ff-pam fs-14px">
                            {t("cloudDiscovery.form.scanTarget")}<span className="text-danger">*</span>
                          </Form.Label>

                          <Select
                            isMulti
                            name="whatToScan"
                            options={scanOptions}
                            value={whatToScan}
                            onChange={handleSelectChange}
                            placeholder={t("cloudDiscovery.form.placeholder.selectOptions")}
                            menuPlacement="bottom"
                            isOptionDisabled={(option) => option.isDisabled}
                            styles={{
                              ...selectStyles,
                              control: (provided, state) => ({
                                ...selectStyles.control(provided, state),
                                minHeight: "38px",
                                fontSize: "14px",
                                paddingTop: "4px",
                                paddingBottom: "4px",
                              }),
                              menuList: (provided) => ({
                                ...selectStyles.menuList(provided),
                                maxHeight: 160,
                              }),
                              option: (provided, state) => ({
                                ...selectStyles.option(provided, state),
                                opacity: state.isDisabled ? 0.5 : 1,
                                cursor: state.isDisabled ? 'not-allowed' : 'default',
                              }),
                            }}
                            theme={selectTheme}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t("cloudDiscovery.form.validation.selectScanTarget")}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Stack>
                </Form>
              </div>
              <div className="border-top p-3 d-flex justify-content-end">
                <GeneralButton
                  color="#505050"
                  variant="outlined"
                  value={t("common.cancel") || "Cancel"}
                  size="large"
                  onClickEvent={cancelAction}
                  className="me-3"
                />
                <GeneralButton
                  type="submit"
                  value={operationType === "edit" ? t("common.update") || "Update" : t("common.save") || "Save"}
                  variant="contained"
                  size="large"
                  disabled={loading}
                  className="me-3"
                  form="cloud_discovery_operations"
                />
              </div>
            </div>
          </Container>
        </Fade>
      </div>
    </Slide>
    </>
  );
}
